import { useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import SEO from '../../components/feature/SEO';

const privacySchema = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'Privacy Policy | Dulair Private Family Office',
  url: `${import.meta.env.VITE_SITE_URL || 'https://dulair.com'}/privacy-policy`,
  description:
    'Dulair Private Family Office privacy policy — how we collect, use, and protect your personal data in compliance with UK GDPR and the Data Protection Act 2018.',
  lastReviewed: '2025-01-01',
  inLanguage: 'en-GB',
  publisher: {
    '@type': 'Organization',
    name: 'Dulair Private Family Office',
    url: import.meta.env.VITE_SITE_URL || 'https://dulair.com',
  },
};

const PrivacyPolicyPage = () => {
  const navigate = useNavigate();

  // Scroll to top when page loads
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Navigate back to home and scroll to footer
  const handleBackToHome = () => {
    navigate('/', { state: { scrollTo: 'footer' } });
  };

  return (
    <div className="min-h-screen bg-cream">
      <SEO
        title="Privacy Policy | Dulair Private Family Office"
        description="Dulair Private Family Office privacy policy — how we collect, use, and protect your personal data in compliance with UK GDPR and the Data Protection Act 2018."
        canonical="/privacy-policy"
        schema={privacySchema}
      />
      {/* Header */}
      <div className="bg-gradient-to-br from-navy to-gray py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-6">
          <button
            onClick={handleBackToHome}
            className="inline-flex items-center gap-2 text-white/70 hover:text-gold transition-colors mb-8 cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i>
            <span className="text-sm">Back to Home</span>
          </button>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="font-serif text-3xl md:text-4xl lg:text-5xl text-white mb-4"
          >
            Privacy Policy
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-white/70 text-sm md:text-base"
          >
            Last updated: January 2025
          </motion.p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-12 md:py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="bg-white rounded-2xl shadow-sm p-6 md:p-10 space-y-8"
        >
          <section>
            <h2 className="font-serif text-xl md:text-2xl text-navy mb-4">1. Introduction</h2>
            <p className="text-text-light text-sm leading-relaxed mb-3">
              Dulair Private Family Office (&ldquo;we&rdquo;, &ldquo;our&rdquo;, or &ldquo;us&rdquo;) is committed to protecting your privacy and personal data. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or engage with our services.
            </p>
            <p className="text-text-light text-sm leading-relaxed">
              We are registered in England and Wales and comply with the UK General Data Protection Regulation (UK GDPR) and the Data Protection Act 2018.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-xl md:text-2xl text-navy mb-4">2. Information We Collect</h2>
            <p className="text-text-light text-sm leading-relaxed mb-3">
              We may collect the following types of information:
            </p>
            <ul className="list-disc list-inside text-text-light text-sm leading-relaxed space-y-2 ml-2">
              <li><strong className="text-navy">Personal Identification Information:</strong> Name, email address, phone number, postal address</li>
              <li><strong className="text-navy">Financial Information:</strong> Investment preferences, net worth declarations (for investor certification purposes)</li>
              <li><strong className="text-navy">Technical Data:</strong> IP address, browser type, device information, pages visited</li>
              <li><strong className="text-navy">Communication Data:</strong> Records of correspondence and enquiries</li>
            </ul>
          </section>

          <section>
            <h2 className="font-serif text-xl md:text-2xl text-navy mb-4">3. How We Use Your Information</h2>
            <p className="text-text-light text-sm leading-relaxed mb-3">
              We use the information we collect for the following purposes:
            </p>
            <ul className="list-disc list-inside text-text-light text-sm leading-relaxed space-y-2 ml-2">
              <li>To provide and maintain our investment advisory services</li>
              <li>To process investor certifications in compliance with FCA regulations</li>
              <li>To respond to your enquiries and provide customer support</li>
              <li>To send you relevant investment opportunities and market insights (with your consent)</li>
              <li>To comply with legal and regulatory obligations</li>
              <li>To improve our website and services</li>
            </ul>
          </section>

          <section>
            <h2 className="font-serif text-xl md:text-2xl text-navy mb-4">4. Legal Basis for Processing</h2>
            <p className="text-text-light text-sm leading-relaxed mb-3">
              We process your personal data on the following legal grounds:
            </p>
            <ul className="list-disc list-inside text-text-light text-sm leading-relaxed space-y-2 ml-2">
              <li><strong className="text-navy">Consent:</strong> Where you have given explicit consent for specific purposes</li>
              <li><strong className="text-navy">Contract:</strong> Where processing is necessary for the performance of a contract</li>
              <li><strong className="text-navy">Legal Obligation:</strong> Where we are required to comply with regulatory requirements</li>
              <li><strong className="text-navy">Legitimate Interests:</strong> Where processing is necessary for our legitimate business interests</li>
            </ul>
          </section>

          <section>
            <h2 className="font-serif text-xl md:text-2xl text-navy mb-4">5. Data Sharing and Disclosure</h2>
            <p className="text-text-light text-sm leading-relaxed mb-3">
              We do not sell your personal information. We may share your data with:
            </p>
            <ul className="list-disc list-inside text-text-light text-sm leading-relaxed space-y-2 ml-2">
              <li>Professional advisors (lawyers, accountants) bound by confidentiality obligations</li>
              <li>Regulatory authorities (such as the FCA) when required by law</li>
              <li>Service providers who assist in our operations under strict data protection agreements</li>
              <li>Third parties in connection with a business transfer or merger</li>
            </ul>
          </section>

          <section>
            <h2 className="font-serif text-xl md:text-2xl text-navy mb-4">6. Data Security</h2>
            <p className="text-text-light text-sm leading-relaxed">
              We implement appropriate technical and organisational measures to protect your personal data against unauthorised access, alteration, disclosure, or destruction. This includes encryption, secure servers, and access controls. However, no method of transmission over the internet is 100% secure, and we cannot guarantee absolute security.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-xl md:text-2xl text-navy mb-4">7. Data Retention</h2>
            <p className="text-text-light text-sm leading-relaxed">
              We retain your personal data only for as long as necessary to fulfil the purposes for which it was collected, including to satisfy legal, regulatory, accounting, or reporting requirements. For investor certification records, we retain data for a minimum of 5 years as required by FCA regulations.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-xl md:text-2xl text-navy mb-4">8. Your Rights</h2>
            <p className="text-text-light text-sm leading-relaxed mb-3">
              Under UK GDPR, you have the following rights:
            </p>
            <ul className="list-disc list-inside text-text-light text-sm leading-relaxed space-y-2 ml-2">
              <li><strong className="text-navy">Right of Access:</strong> Request a copy of your personal data</li>
              <li><strong className="text-navy">Right to Rectification:</strong> Request correction of inaccurate data</li>
              <li><strong className="text-navy">Right to Erasure:</strong> Request deletion of your data (subject to legal obligations)</li>
              <li><strong className="text-navy">Right to Restrict Processing:</strong> Request limitation of how we use your data</li>
              <li><strong className="text-navy">Right to Data Portability:</strong> Receive your data in a structured format</li>
              <li><strong className="text-navy">Right to Object:</strong> Object to processing based on legitimate interests</li>
              <li><strong className="text-navy">Right to Withdraw Consent:</strong> Withdraw consent at any time</li>
            </ul>
          </section>

          <section>
            <h2 className="font-serif text-xl md:text-2xl text-navy mb-4">9. Cookies</h2>
            <p className="text-text-light text-sm leading-relaxed">
              Our website uses cookies to enhance your browsing experience. Cookies are small text files stored on your device. We use essential cookies for website functionality and analytics cookies to understand how visitors interact with our site. You can manage cookie preferences through your browser settings.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-xl md:text-2xl text-navy mb-4">10. Contact Us</h2>
            <p className="text-text-light text-sm leading-relaxed mb-3">
              If you have any questions about this Privacy Policy or wish to exercise your rights, please contact us:
            </p>
            <div className="bg-cream rounded-xl p-5 space-y-2">
              <p className="text-navy text-sm font-medium">Dulair Private Family Office</p>
              <p className="text-text-light text-sm">Kings Head House, 15 London End</p>
              <p className="text-text-light text-sm">Beaconsfield, Buckinghamshire, HP9 2HN</p>
              <p className="text-text-light text-sm">Email: enquiries@dulairpfo.com</p>
              <p className="text-text-light text-sm">Phone: +44 203 151 0000</p>
            </div>
          </section>

          <section>
            <h2 className="font-serif text-xl md:text-2xl text-navy mb-4">11. Changes to This Policy</h2>
            <p className="text-text-light text-sm leading-relaxed">
              We may update this Privacy Policy from time to time. Any changes will be posted on this page with an updated revision date. We encourage you to review this policy periodically to stay informed about how we protect your information.
            </p>
          </section>
        </motion.div>

        {/* Back to Home Button */}
        <div className="mt-8 text-center">
          <button
            onClick={handleBackToHome}
            className="inline-flex items-center gap-2 px-6 py-3 bg-navy text-white rounded-full hover:bg-gold hover:text-navy transition-all duration-300 cursor-pointer text-sm font-medium"
          >
            <i className="ri-arrow-left-line"></i>
            Back to Home
          </button>
        </div>
      </div>

      {/* Simple Footer */}
      <footer className="bg-navy py-6">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <p className="text-white/50 text-xs">
            © 2025 Dulair Private Family Office. All Rights Reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicyPage;
